/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import classNames from 'classnames';
import PropTypes from "prop-types";
import React, { Component } from 'react';
import { EuiScreenReaderOnly } from '../accessibility';
import { EuiBreadcrumbs } from '../breadcrumbs';
import { EuiButton, EuiButtonIcon } from '../button';
import { EuiI18n } from '../i18n';
import { EuiIcon } from '../icon';
import { EuiPortal } from '../portal';

/**
 * Extends EuiButton excluding `size`. Requires `label` as the `children`.
 */

/**
 * Creates a `button` visually styles as a tab.
 * Requires `label` as the `children`.
 * `onClick` must be provided to handle the content swapping.
 */

/**
 * Extends EuiBreadcrumbs
 */

/**
 * Simple div controlling color and size text output.
 * Requires `label` as the `children`.
 */

/**
 * Custom props specific to the icon control type
 */

/**
 * Icon can extend EuiIcon
 * Had to omit `onClick` as it's a valid prop of SVGElement
 * Also omits `type` and `id` as these are also specific to icon control
 */

/**
 * Icon can extend EuiButtonIcon
 * Also omits `iconType` and `id` as these are also specific to icon control
 */

export class EuiControlBar extends Component {
  static defaultProps = {
    leftOffset: 0,
    rightOffset: 0,
    position: 'fixed',
    size: 'l',
    showContent: false,
    showOnMobile: false
  };
  bar = null;
  componentDidMount() {
    if (this.props.position === 'fixed') {
      const height = this.bar ? this.bar.clientHeight : -1;
      document.body.style.paddingBottom = `${height}px`;
      if (this.props.bodyClassName) {
        document.body.classList.add(this.props.bodyClassName);
      }
    }
  }
  componentWillUnmount() {
    document.body.style.paddingBottom = '';
    if (this.props.bodyClassName) {
      document.body.classList.remove(this.props.bodyClassName);
    }
  }
  state = {
    selectedTab: ''
  };
  render() {
    const {
      children,
      className,
      showContent,
      controls,
      size,
      leftOffset,
      rightOffset,
      maxHeight,
      showOnMobile,
      style,
      position,
      bodyClassName,
      landmarkHeading,
      ...rest
    } = this.props;
    const styles = {
      ...style,
      left: leftOffset,
      right: rightOffset,
      maxHeight: maxHeight
    };
    const classes = classNames('euiControlBar', className, {
      'euiControlBar-isOpen': showContent,
      'euiControlBar--large': size === 'l',
      'euiControlBar--medium': size === 'm',
      'euiControlBar--small': size === 's',
      'euiControlBar--fixed': position === 'fixed',
      'euiControlBar--absolute': position === 'absolute',
      'euiControlBar--relative': position === 'relative',
      'euiControlBar--showOnMobile': showOnMobile
    });
    const handleTabClick = (control, e) => {
      this.setState({
        selectedTab: control.id
      }, () => {
        control.onClick(e);
      });
    };
    const controlItem = (control, index) => {
      switch (control.controlType) {
        case 'button':
          {
            const {
              controlType,
              id,
              color = 'ghost',
              label,
              className,
              ...rest
            } = control;
            return <EuiButton key={id + index} className={classNames('euiControlBar__button', className)} color={color} {...rest} size="s">
              {label}
            </EuiButton>;
          }
        case 'icon':
          {
            const {
              controlType,
              id,
              iconType,
              className,
              color = 'ghost',
              onClick,
              href,
              ...rest
            } = control;
            return onClick || href ? <EuiButtonIcon key={id + index} className={classNames('euiControlBar__buttonIcon', className)} onClick={onClick} href={href} color={color} {...rest} iconType={iconType} /> : <EuiIcon key={id + index} className={classNames('euiControlBar__icon', className)} type={iconType} color={color} {...rest} />;
          }
        case 'divider':
          return <div key={control.controlType + index} className="euiControlBar__divider" />;
        case 'spacer':
          return <div key={control.controlType + index} className="euiControlBar__spacer" />;
        case 'text':
          {
            const {
              controlType,
              id,
              text,
              className,
              ...rest
            } = control;
            return <div key={id} className={classNames('euiControlBar__text', className)} {...rest}>
              {text}
            </div>;
          }
        case 'tab':
          {
            const {
              controlType,
              id,
              label,
              onClick,
              className,
              ...rest
            } = control;
            const tabClasses = classNames('euiControlBar__tab', {
              'euiControlBar__tab--active': showContent && id === this.state.selectedTab
            }, className);
            return <button key={id + index} className={tabClasses} onClick={event => handleTabClick(control, event)} {...rest}>
              {label}
            </button>;
          }
        case 'breadcrumbs':
          {
            const {
              controlType,
              id,
              ...rest
            } = control;
            return <EuiBreadcrumbs className="euiControlBar__breadcrumbs" key={control.id} {...rest} />;
          }
      }
    };
    const controlBar = <EuiI18n token="euiControlBar.screenReaderHeading" default="Page level controls">
        {screenReaderHeading =>
      // Though it would be better to use aria-labelledby than aria-label and not repeat the same string twice
      // A bug in voiceover won't list some landmarks in the rotor without an aria-label
      <section className={classes} aria-label={landmarkHeading ? landmarkHeading : screenReaderHeading} {...rest} style={styles}>
            <EuiScreenReaderOnly>
              <h2>{landmarkHeading ? landmarkHeading : screenReaderHeading}</h2>
            </EuiScreenReaderOnly>
            <div className="euiControlBar__controls" ref={node => {
          this.bar = node;
        }}>
              {controls.map((control, index) => controlItem(control, index))}
            </div>
            {this.props.showContent ? <div className="euiControlBar__content">{children}</div> : null}
          </section>}
      </EuiI18n>;
    return position === 'fixed' ? <EuiPortal>
        {controlBar}
        <EuiScreenReaderOnly>
          <p aria-live="assertive">
            {landmarkHeading ? <EuiI18n token="euiControlBar.customScreenReaderAnnouncement" default="There is a new region landmark called {landmarkHeading} with page level controls at the end of the document." values={{
            landmarkHeading
          }} /> : <EuiI18n token="euiControlBar.screenReaderAnnouncement" default="There is a new region landmark with page level controls at the end of the document." />}
          </p>
        </EuiScreenReaderOnly>
      </EuiPortal> : controlBar;
  }
}
EuiControlBar.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  /**
       * Show or hide the content area containing the `children`
       */
  showContent: PropTypes.bool,
  /**
       * An array of controls, actions, and layout spacers to display.
       * Accepts `'button' | 'tab' | 'breadcrumbs' | 'text' | 'icon' | 'spacer' | 'divider'`
       */
  controls: PropTypes.arrayOf(PropTypes.shape({
    href: PropTypes.string,
    onClick: PropTypes.func,
    id: PropTypes.string,
    label: PropTypes.oneOfType([PropTypes.node.isRequired, PropTypes.node]),
    buttonRef: PropTypes.any,
    controlType: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.oneOf(["button"]).isRequired, PropTypes.oneOfType([PropTypes.oneOf(["breadcrumbs"]).isRequired, PropTypes.oneOf(["tab"]).isRequired]).isRequired]).isRequired, PropTypes.oneOf(["text"]).isRequired]).isRequired, PropTypes.oneOf(["icon"]).isRequired]).isRequired, PropTypes.oneOf(["divider"]).isRequired]).isRequired, PropTypes.oneOf(["spacer"]).isRequired]).isRequired,
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any,
    /**
       * Hides extra (above the max) breadcrumbs under a collapsed item as the window gets smaller.
       * Pass a custom #EuiBreadcrumbResponsiveMaxCount object to change the number of breadcrumbs to show at the particular breakpoints.
       *
       * Pass `false` to turn this behavior off.
       *
       * Default: `{ xs: 1, s: 2, m: 4 }`
       */
    responsive: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.any.isRequired]),
    /**
       * Forces all breadcrumbs to single line and
       * truncates each breadcrumb to a particular width,
       * except for the last item
       */
    truncate: PropTypes.bool,
    /**
       * Collapses the inner items past the maximum set here
       * into a single ellipses item.
       * Omitting or passing a `0` value will show all breadcrumbs.
       */
    max: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.oneOf([null])]),
    /**
       * The array of individual #EuiBreadcrumb items
       */
    breadcrumbs: PropTypes.arrayOf(PropTypes.shape({
      className: PropTypes.string,
      "aria-label": PropTypes.string,
      "data-test-subj": PropTypes.string,
      css: PropTypes.any,
      href: PropTypes.string,
      rel: PropTypes.string,
      onClick: PropTypes.func,
      /**
           * Visible label of the breadcrumb
           */
      text: PropTypes.node.isRequired,
      /**
           * Force a max-width on the breadcrumb text
           */
      truncate: PropTypes.bool,
      /**
           * Accepts any EuiLink `color` when rendered as one (has `href`, `onClick`, or `popoverContent`)
           */
      color: PropTypes.any,
      /**
           * Override the existing `aria-current` which defaults to `page` for the last breadcrumb
           */
      "aria-current": PropTypes.any,
      /**
           * Creates a breadcrumb that toggles a popover dialog
           *
           * If passed, both `href` and `onClick` will be ignored - the breadcrumb's
           * click behavior should only trigger a popover.
           */
      popoverContent: PropTypes.node,
      /**
           * Allows customizing the popover if necessary. Accepts any props that
           * [EuiPopover](/#/layout/popover) accepts, except for props that control state.
           */
      popoverProps: PropTypes.shape({
        /**
           * Class name passed to the direct parent of the button
           */
        anchorClassName: PropTypes.string,
        /**
           * Alignment of the popover and arrow relative to the button
           */
        anchorPosition: PropTypes.any,
        /**
           * Style and position alteration for arrow-less attachment.
           * Intended for use with inputs as anchors, e.g. EuiInputPopover
           */
        attachToAnchor: PropTypes.bool,
        buttonRef: PropTypes.any,
        /**
           * Restrict the popover's position within this element
           */
        container: PropTypes.any,
        /**
           * CSS display type for both the popover and anchor
           */
        display: PropTypes.any,
        /**
           * Object of props passed to EuiFocusTrap
           */
        focusTrapProps: PropTypes.any,
        /**
           * Show arrow indicating to originating button
           */
        hasArrow: PropTypes.bool,
        /**
           * Specifies what element should initially have focus; Can be a DOM
           * node, or a selector string (which will be passed to
           * document.querySelector() to find the DOM node), or a function that
           * returns a DOM node.
           *
           * If not passed, initial focus defaults to the popover panel.
           */
        initialFocus: PropTypes.any,
        /**
           * Passed directly to EuiPortal for DOM positioning. Both properties are
           * required if prop is specified
           */
        insert: PropTypes.shape({
          sibling: PropTypes.any.isRequired,
          position: PropTypes.oneOf(["before", "after"]).isRequired
        }),
        /**
           * Traps tab focus within the popover contents
           */
        ownFocus: PropTypes.bool,
        /**
           * Custom class added to the EuiPanel containing the popover contents
           */
        panelClassName: PropTypes.string,
        /**
           * EuiPanel padding on all sides
           */
        panelPaddingSize: PropTypes.any,
        /**
           * Standard DOM `style` attribute. Passed to the EuiPanel
           */
        panelStyle: PropTypes.any,
        /**
           * Object of props passed to EuiPanel. See #EuiPopoverPanelProps
           */
        panelProps: PropTypes.shape({
          element: PropTypes.oneOf(["div"]),
          /**
             * Padding for all four sides
             */
          paddingSize: PropTypes.any,
          /**
             * Corner border radius
             */
          borderRadius: PropTypes.any,
          /**
             * When true the panel will grow in height to match `EuiFlexItem`
             */
          grow: PropTypes.bool,
          panelRef: PropTypes.any,
          /**
             * Background color of the panel;
             * Usually a lightened form of the brand colors
             */
          color: PropTypes.any,
          className: PropTypes.string,
          "aria-label": PropTypes.string,
          "data-test-subj": PropTypes.string,
          css: PropTypes.any
        }),
        panelRef: PropTypes.any,
        /**
           * Optional screen reader instructions to announce upon popover open,
           * in addition to EUI's default popover instructions for Escape on close.
           * Useful for popovers that may have additional keyboard capabilities such as
           * arrow navigation.
           */
        popoverScreenReaderText: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.node.isRequired]),
        popoverRef: PropTypes.any,
        /**
           * When `true`, the popover's position is re-calculated when the user
           * scrolls, this supports having fixed-position popover anchors. When nesting
           * an `EuiPopover` in a scrollable container, `repositionOnScroll` should be `true`
           */
        repositionOnScroll: PropTypes.bool,
        /**
           * Must be set to true if using `EuiDragDropContext` within a popover,
           * otherwise your nested drag & drop will have incorrect positioning
           */
        hasDragDrop: PropTypes.bool,
        /**
           * By default, popover content inherits the z-index of the anchor
           * component; pass `zIndex` to override
           */
        zIndex: PropTypes.number,
        /**
           * Distance away from the anchor that the popover will render
           */
        offset: PropTypes.number,
        /**
           * Minimum distance between the popover and the bounding container;
           * Pass an array of 4 values to adjust each side differently: `[top, right, bottom, left]`
           * Default is 16
           */
        buffer: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.any.isRequired]),
        /**
           * Element to pass as the child element of the arrow;
           * Use case is typically limited to an accompanying `EuiBeacon`
           */
        arrowChildren: PropTypes.node,
        /**
           * Provide a name to the popover panel
           */
        "aria-label": PropTypes.string,
        /**
           * Alternative option to `aria-label` that takes an `id`.
           * Usually takes the `id` of the popover title
           */
        "aria-labelledby": PropTypes.string,
        /**
           * Function callback for when the popover positon changes
           */
        onPositionChange: PropTypes.func,
        className: PropTypes.string,
        "data-test-subj": PropTypes.string,
        css: PropTypes.any
      })
    }).isRequired),
    /**
       * Determines breadcrumbs appearance, with `page` being the default styling.
       * Application breadcrumbs should only be once per page, in (e.g.) EuiHeader
       */
    type: PropTypes.oneOf(["page", "application"]),
    /**
       * Whether the last breadcrumb should visually (and accessibly, to screen readers)
       * be highlighted as the current page. Defaults to true.
       */
    lastBreadcrumbIsCurrentPage: PropTypes.bool,
    text: PropTypes.node,
    iconType: PropTypes.string
  }).isRequired).isRequired,
  /**
       * The default height of the content area.
       */
  size: PropTypes.oneOf(["s", "m", "l"]),
  /**
       * Customize the max height.
       * Best when used with `size=l` as this will ensure the actual height equals the max height set.
       */
  maxHeight: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.string.isRequired]),
  /**
       * Set the offset from the left side of the screen.
       */
  leftOffset: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.string.isRequired]),
  /**
       * Set the offset from the left side of the screen.
       */
  rightOffset: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.string.isRequired]),
  /**
       * The control bar is hidden on mobile by default. Use the `showOnMobile` prop to force it's display on mobile screens.
       * You'll need to ensure that the content you place into the bar renders as expected on mobile.
       */
  showOnMobile: PropTypes.bool,
  /**
       * By default EuiControlBar will live in a portal, fixed position to the browser window.
       * Change the position of the bar to live inside a container and be positioned against its parent.
       */
  position: PropTypes.oneOf(["fixed", "relative", "absolute"]),
  /**
       * Optional class applied to the body used when `position = fixed`
       */
  bodyClassName: PropTypes.string,
  /**
       * Customize the screen reader heading that helps users find this control. Default is "Page level controls".
       */
  landmarkHeading: PropTypes.string
};
try {
  EuiControlBar.__docgenInfo = {
    tags: {},
    filePath: '/Users/briannahall/Documents/EUI/eui/src/components/control_bar/control_bar.tsx',
    description: '',
    displayName: 'EuiControlBar',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        },
        declarations: [{
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        },
        declarations: [{
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      showContent: {
        defaultValue: {
          value: 'false'
        },
        description: 'Show or hide the content area containing the `children`',
        name: 'showContent',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/control_bar/control_bar.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      controls: {
        defaultValue: null,
        description: 'An array of controls, actions, and layout spacers to display.\n' + "Accepts `'button' | 'tab' | 'breadcrumbs' | 'text' | 'icon' | 'spacer' | 'divider'`",
        name: 'controls',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/control_bar/control_bar.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: '((DisambiguateSet<(DisambiguateSet<(DisambiguateSet<(DisambiguateSet<(DisambiguateSet<ButtonControlProps, (DisambiguateSet<BreadcrumbControl, TabControl> & TabControl) | (DisambiguateSet<...> & BreadcrumbControl)> & ((DisambiguateSet<...> & TabControl) | (DisambiguateSet<...> & BreadcrumbControl))) | (DisambiguateSe...'
        }
      },
      size: {
        defaultValue: {
          value: 'l'
        },
        description: 'The default height of the content area.',
        name: 'size',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/control_bar/control_bar.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "m" | "l"',
          value: [{
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"l"'
          }]
        }
      },
      maxHeight: {
        defaultValue: null,
        description: 'Customize the max height.\n' + 'Best when used with `size=l` as this will ensure the actual height equals the max height set.',
        name: 'maxHeight',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/control_bar/control_bar.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string | number'
        }
      },
      leftOffset: {
        defaultValue: {
          value: '0'
        },
        description: 'Set the offset from the left side of the screen.',
        name: 'leftOffset',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/control_bar/control_bar.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string | number'
        }
      },
      rightOffset: {
        defaultValue: {
          value: '0'
        },
        description: 'Set the offset from the left side of the screen.',
        name: 'rightOffset',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/control_bar/control_bar.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string | number'
        }
      },
      showOnMobile: {
        defaultValue: {
          value: 'false'
        },
        description: "The control bar is hidden on mobile by default. Use the `showOnMobile` prop to force it's display on mobile screens.\n" + "You'll need to ensure that the content you place into the bar renders as expected on mobile.",
        name: 'showOnMobile',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/control_bar/control_bar.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      position: {
        defaultValue: {
          value: 'fixed'
        },
        description: 'By default EuiControlBar will live in a portal, fixed position to the browser window.\n' + 'Change the position of the bar to live inside a container and be positioned against its parent.',
        name: 'position',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/control_bar/control_bar.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"fixed" | "absolute" | "relative"',
          value: [{
            value: '"fixed"'
          }, {
            value: '"absolute"'
          }, {
            value: '"relative"'
          }]
        }
      },
      bodyClassName: {
        defaultValue: null,
        description: 'Optional class applied to the body used when `position = fixed`',
        name: 'bodyClassName',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/control_bar/control_bar.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      landmarkHeading: {
        defaultValue: null,
        description: 'Customize the screen reader heading that helps users find this control. Default is "Page level controls".',
        name: 'landmarkHeading',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/control_bar/control_bar.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      }
    },
    extendedInterfaces: ['HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'CommonProps']
  };
} catch (e) {}